import { FACEBOOK_APP_ID } from '../config/facebook';
import { supabase } from './supabaseClient';

// Backend API URL for token exchange
const API_URL = process.env.REACT_APP_API_URL || 'https://toldfel.hu';

const facebookAuth = {
  signInWithFacebook() {
    const redirectTo = `${window.location.origin}/`;
    // Facebook + Instagram permissions
    const scope = [
      'public_profile',
      'pages_show_list',
      'pages_read_engagement',
      'pages_manage_posts',
      'instagram_basic',
      'instagram_content_publish'
    ].join(',');
    
    // Add state parameter to distinguish from Supabase OAuth and prevent CSRF
    const state = 'facebook_oauth_' + Date.now();
    sessionStorage.setItem('facebook_oauth_state', state);

    const authUrl = `https://www.facebook.com/v21.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&redirect_uri=${encodeURIComponent(redirectTo)}&scope=${encodeURIComponent(scope)}&response_type=token&state=${encodeURIComponent(state)}`;
    
    // Debug: Log the OAuth URL before navigation
    console.log('Facebook OAuth URL:', authUrl);
    console.log('Redirect URI:', redirectTo);
    console.log('App ID:', FACEBOOK_APP_ID);
    
    // Force navigation - use replace to avoid back button issues
    window.location.replace(authUrl);
  },

  // Parse Facebook access_token from URL hash and store it
  async handleFacebookCallback() {
    const hash = window.location.hash || '';
    if (!hash) return null;

    const params = new URLSearchParams(hash.substring(1));
    const state = params.get('state');
    const accessToken = params.get('access_token');
    const expiresIn = params.get('expires_in');
    const error = params.get('error');
    const errorReason = params.get('error_reason');
    const errorDescription = params.get('error_description');

    // Debug logging for callback parameters
    console.log('Facebook callback check - URL hash params:', { 
      hasAccessToken: !!accessToken, 
      state, 
      error, 
      errorReason,
      errorDescription 
    });

    // Only process if this is a Facebook OAuth callback (has our state parameter)
    const savedState = sessionStorage.getItem('facebook_oauth_state');
    if (!state || !savedState || !state.startsWith('facebook_oauth_')) {
      return null; // Not a Facebook OAuth callback, ignore
    }

    // Handle OAuth errors from Facebook
    if (error) {
      console.error('Facebook OAuth error:', error, errorReason, errorDescription);
      sessionStorage.removeItem('facebook_oauth_state');
      // Clean URL
      try {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      } catch (e) {}
      return { error: errorDescription || errorReason || error };
    }

    // Verify state matches (CSRF protection)
    if (state !== savedState) {
      console.error('OAuth state mismatch - stored:', savedState, 'received:', state);
      return { error: 'Security verification failed. Please try again.' };
    }

    // Need access token to proceed
    if (!accessToken) {
      console.error('Facebook callback missing access token');
      sessionStorage.removeItem('facebook_oauth_state');
      return { error: 'No access token received from Facebook' };
    }

    if (accessToken) {
      sessionStorage.removeItem('facebook_oauth_state');
      
      // Clean URL first
      try {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      } catch (e) {}

      // Try to exchange for long-lived token via backend
      const session = supabase.getSession();
      if (session && session.user && session.user.id) {
        try {
          console.log('Exchanging short-lived token for long-lived token...');
          const exchangeResponse = await fetch(`${API_URL}/api/facebook/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              short_lived_token: accessToken,
              user_id: session.user.id
            })
          });

          if (exchangeResponse.ok) {
            const exchangeData = await exchangeResponse.json();
            console.log('Successfully exchanged for long-lived token, expires:', exchangeData.expires_at);
            
            const tokenInfo = {
              access_token: exchangeData.access_token,
              expires_at: exchangeData.expires_at,
              user_name: exchangeData.user_name || null
            };
            localStorage.setItem('facebook_token', JSON.stringify(tokenInfo));
            return tokenInfo;
          } else {
            console.error('Failed to exchange token:', await exchangeResponse.text());
          }
        } catch (e) {
          console.error('Error exchanging token:', e);
        }
      }

      // Fallback: store short-lived token if exchange fails or no user session
      const expiresAt = expiresIn ? new Date(Date.now() + parseInt(expiresIn, 10) * 1000).toISOString() : null;
      const tokenInfo = { access_token: accessToken, expires_in: expiresIn, expires_at: expiresAt };
      
      // Store in localStorage
      localStorage.setItem('facebook_token', JSON.stringify(tokenInfo));

      // Store in Supabase if user is logged in
      try {
        if (session && session.user && session.user.id) {
          console.log('Storing short-lived Facebook token in Supabase for user:', session.user.id);
          
          await supabase.upsertPlatformToken({
            user_id: session.user.id,
            platform: 'facebook',
            access_token: accessToken,
            refresh_token: null,
            expires_at: expiresAt,
            meta: null
          });
          
          console.log('Facebook token stored in Supabase successfully');
        } else {
          console.log('No user session - Facebook token stored only in localStorage');
        }
      } catch (e) {
        console.error('Failed to store Facebook token in Supabase:', e);
      }

      return tokenInfo;
    }
    return null;
  },

  getToken() {
    const raw = localStorage.getItem('facebook_token');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  },

  // Check if the token is expired or about to expire (within 1 hour)
  isTokenExpired() {
    const token = this.getToken();
    if (!token) {
      console.log('[FB Token] No token found');
      return true;
    }
    
    // If no expires_at, we can't determine expiration - let the API call fail if needed
    if (!token.expires_at) {
      console.log('[FB Token] No expires_at field - will attempt to use token anyway');
      return false; // Changed from true to false - try to use it
    }
    
    const expiresAt = new Date(token.expires_at);
    const now = new Date();
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    
    const isExpired = expiresAt <= oneHourFromNow;
    console.log('[FB Token] Expiration check:', {
      expires_at: token.expires_at,
      expiresAt: expiresAt.toLocaleString(),
      now: now.toLocaleString(),
      oneHourFromNow: oneHourFromNow.toLocaleString(),
      isExpired
    });
    
    return isExpired;
  },

  // Get token expiration info for display
  getTokenExpirationInfo() {
    const token = this.getToken();
    if (!token || !token.expires_at) return null;
    
    const expiresAt = new Date(token.expires_at);
    const now = new Date();
    const diffMs = expiresAt - now;
    
    if (diffMs <= 0) return { expired: true, message: 'Token lejárt' };
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return { expired: false, message: `${diffDays} nap ${diffHours} óra múlva jár le` };
    } else if (diffHours > 0) {
      return { expired: false, message: `${diffHours} óra múlva jár le` };
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return { expired: false, message: `${diffMinutes} perc múlva jár le` };
    }
  },

  async loadTokenFromSupabase() {
    try {
      const session = supabase.getSession();
      if (!session || !session.user || !session.user.id) {
        return null;
      }

      const tokens = await supabase.fetchPlatformTokens(session.user.id);
      const facebookToken = tokens.find(t => t.platform === 'facebook');
      
      if (facebookToken && facebookToken.access_token) {
        console.log('Loaded Facebook token from Supabase');
        const tokenInfo = {
          access_token: facebookToken.access_token,
          expires_at: facebookToken.expires_at
        };
        localStorage.setItem('facebook_token', JSON.stringify(tokenInfo));
        return tokenInfo;
      }
      
      return null;
    } catch (e) {
      console.error('Failed to load Facebook token from Supabase:', e);
      return null;
    }
  },

  /**
   * Refresh the long-lived token by re-exchanging with backend
   * Facebook doesn't have automatic refresh tokens, so we need to prompt re-authentication if token expires
   * This function can be called to check token validity and attempt to extend it
   */
  async refreshToken() {
    const token = this.getToken();
    if (!token || !token.access_token) {
      console.log('No token to refresh');
      return null;
    }

    // If token isn't expired yet, no need to refresh
    if (!this.isTokenExpired()) {
      console.log('Token is still valid, no refresh needed');
      return token;
    }

    // If expired, user needs to re-authenticate
    // Facebook long-lived tokens cannot be automatically refreshed
    console.warn('Facebook token has expired. User needs to re-authenticate.');
    return null;
  },

  async signOut() {
    localStorage.removeItem('facebook_token');
    
    // Remove from Supabase
    try {
      const session = supabase.getSession();
      if (session && session.user && session.user.id) {
        await supabase.deletePlatformToken(session.user.id, 'facebook');
        console.log('Facebook token deleted from Supabase');
      }
    } catch (e) {
      console.error('Failed to delete Facebook token from Supabase:', e);
    }
  }
};

export default facebookAuth;
